import { Trash2 } from "lucide-react";
import { useMemo, useState } from "react";

import { ConfirmButton } from "../components/ConfirmButton";
import { ErrorBanner } from "../components/ErrorBanner";
import { SectionLabel } from "../components/SectionLabel";
import { Skeleton } from "../components/Skeleton";
import { useDeleteHarvest, useHarvests, usePatchHarvest } from "../lib/queries";
import { formatDisplay } from "../lib/date";
import { fonts, radius, shadow, tokens, trayColors } from "../styles/theme";

export function YieldView() {
  const harvests = useHarvests();
  const patchHarvest = usePatchHarvest();
  const deleteHarvest = useDeleteHarvest();
  const [error, setError] = useState<string | null>(null);

  const stats = useMemo(() => {
    const byCrop = new Map<
      string,
      { count: number; logged: number; totalYield: number; totalSeed: number }
    >();
    for (const h of harvests.data ?? []) {
      const s = byCrop.get(h.crop) ?? { count: 0, logged: 0, totalYield: 0, totalSeed: 0 };
      s.count++;
      s.totalSeed += h.seed_oz;
      if (h.yield_oz !== null) {
        s.logged++;
        s.totalYield += h.yield_oz;
      }
      byCrop.set(h.crop, s);
    }
    return Array.from(byCrop.entries()).map(([crop, s]) => ({
      crop,
      count: s.count,
      logged: s.logged,
      avgRatio: s.logged > 0 && s.totalSeed > 0 ? (s.totalYield / s.totalSeed).toFixed(2) : "—",
      totalYield: s.totalYield.toFixed(1),
    }));
  }, [harvests.data]);

  return (
    <div>
      {error && <ErrorBanner message={error} onDismiss={() => setError(null)} />}

      {harvests.isLoading && <Skeleton count={6} height={60} />}
      {harvests.isError && (
        <ErrorBanner message={`Failed to load harvests: ${(harvests.error as Error).message}`} />
      )}

      {stats.length > 0 && (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
            gap: "12px",
            marginBottom: "24px",
          }}
        >
          {stats.map((s) => (
            <div
              key={s.crop}
              style={{
                padding: "16px 18px",
                background: tokens.card,
                border: `1px solid ${tokens.line}`,
                borderRadius: radius.lg,
                boxShadow: shadow.xs,
              }}
            >
              <div
                style={{
                  fontSize: "11px",
                  color: tokens.muted,
                  letterSpacing: "0.14em",
                  marginBottom: "8px",
                  fontWeight: 600,
                  textTransform: "uppercase",
                }}
              >
                {s.crop}
              </div>
              <div
                style={{
                  fontSize: "28px",
                  color: tokens.ink,
                  lineHeight: 1.0,
                }}
              >
                {s.avgRatio}
                <span style={{ fontSize: "18px", color: tokens.sage, marginLeft: "2px" }}>×</span>
              </div>
              <div
                style={{
                  fontFamily: fonts.mono,
                  fontSize: "11px",
                  color: tokens.muted,
                  marginTop: "6px",
                }}
              >
                {s.logged}/{s.count} logged · {s.totalYield} oz
              </div>
            </div>
          ))}
        </div>
      )}

      <SectionLabel>Harvest log</SectionLabel>
      <div
        style={{
          border: `1px solid ${tokens.line}`,
          borderRadius: radius.md,
          background: tokens.card,
          overflow: "hidden",
          boxShadow: shadow.xs,
        }}
      >
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1.2fr 0.5fr 1.5fr 0.8fr 1fr 0.8fr 44px",
            gap: "8px",
            padding: "10px 14px",
            background: tokens.sunken,
            fontSize: "10px",
            letterSpacing: "0.14em",
            color: tokens.muted,
            fontWeight: 600,
            textTransform: "uppercase",
            borderBottom: `1px solid ${tokens.line}`,
          }}
        >
          <span>Date</span>
          <span>Tray</span>
          <span>Crop</span>
          <span>Seed oz</span>
          <span>Yield oz</span>
          <span>Ratio</span>
          <span />
        </div>
        {(harvests.data ?? []).map((h) => {
          const ratio =
            h.yield_oz !== null && h.seed_oz > 0 ? (h.yield_oz / h.seed_oz).toFixed(2) : null;
          const trayColor = h.tray_id ? (trayColors[h.tray_id] ?? tokens.faint) : tokens.faint;
          return (
            <div
              key={h.id}
              style={{
                display: "grid",
                gridTemplateColumns: "1.2fr 0.5fr 1.5fr 0.8fr 1fr 0.8fr 44px",
                gap: "8px",
                padding: "10px 14px",
                borderBottom: `1px solid ${tokens.line}`,
                fontSize: "13px",
                color: tokens.inkSoft,
                alignItems: "center",
              }}
            >
              <span style={{ color: tokens.muted, fontFamily: fonts.mono, fontSize: "12px" }}>
                {formatDisplay(h.date)}
              </span>
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "6px",
                  color: tokens.inkSoft,
                  fontWeight: 600,
                  fontSize: "12px",
                }}
              >
                <span
                  aria-hidden
                  style={{
                    width: "8px",
                    height: "8px",
                    borderRadius: radius.pill,
                    background: trayColor,
                  }}
                />
                {h.tray_id ?? "—"}
              </span>
              <span>{h.crop}</span>
              <span style={{ fontFamily: fonts.mono }}>{h.seed_oz}</span>
              <YieldInput
                value={h.yield_oz}
                onCommit={(v) =>
                  patchHarvest.mutate(
                    { id: h.id, patch: { yield_oz: v } },
                    { onError: (err) => setError(err.message) },
                  )
                }
              />
              <span
                style={{
                  color: ratio ? tokens.sageDeep : tokens.faint,
                  fontFamily: fonts.mono,
                  fontWeight: 500,
                }}
              >
                {ratio ? `${ratio}×` : "—"}
              </span>
              <ConfirmButton
                ariaLabel={`Delete harvest ${h.crop} on ${h.date}`}
                onConfirm={() =>
                  deleteHarvest.mutate({ id: h.id }, { onError: (err) => setError(err.message) })
                }
                confirmLabel={
                  <span style={{ color: tokens.coral, fontWeight: 600, fontSize: "12px" }}>
                    Yes
                  </span>
                }
                style={{
                  background: "transparent",
                  border: "none",
                  color: tokens.faint,
                  fontSize: "11px",
                  padding: 0,
                  borderRadius: radius.sm,
                }}
              >
                <Trash2 size={14} />
              </ConfirmButton>
            </div>
          );
        })}
        {(harvests.data ?? []).length === 0 && !harvests.isLoading && (
          <div style={{ padding: "24px", color: tokens.muted, fontSize: "13px" }}>
            No harvests yet.
          </div>
        )}
      </div>
    </div>
  );
}

function YieldInput({
  value,
  onCommit,
}: {
  value: number | null;
  onCommit: (v: number | null) => void;
}) {
  const [draft, setDraft] = useState<string>(value !== null ? String(value) : "");

  const commit = () => {
    const trimmed = draft.trim();
    const next = trimmed === "" ? null : Number.parseFloat(trimmed);
    if (next !== null && Number.isNaN(next)) return;
    if (next === value) return;
    onCommit(next);
  };

  return (
    <input
      type="number"
      step="0.1"
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === "Enter") (e.target as HTMLInputElement).blur();
      }}
      placeholder="—"
      aria-label="Yield in oz"
      style={{
        background: tokens.page,
        border: `1px solid ${tokens.lineStrong}`,
        borderRadius: radius.sm,
        color: tokens.ink,
        padding: "10px 12px",
        minHeight: "44px",
        fontFamily: fonts.mono,
        fontSize: "13px",
        width: "100%",
      }}
    />
  );
}
